"use client";

import React from "react";

import { InvoiceJson, InvoicePublicJson } from "@/types/Invoice.type";

import PlusIcon from "@/components/icons/PlusIcon";
import MinusIcon from "@/components/icons/MinusIcon";
import TextDateTime from "@/components/TextDateTime";
import InvoicePublicDetails from "./InvoicePublicDetails";
import Money from "@/components/ui/Money";
import {
  PendingIcon,
  SuccessIcon,
  SuccessOutlineIcon,
} from "@/components/icons";
import CloseCircleFilledIcon from "@/components/icons/CloseCircleFilledIcon";
import useTranslationApi from "@/lib/hooks/useTranslationApi";

type Props = {
  open?: boolean;
  onClick?: (v: any) => void;
  data: InvoiceJson;
};

export default function InvoicePublicCard({ open, onClick, data }: Props) {
  const { t } = useTranslationApi([
    "order_status_delivery",
    "order_status_pending",
    "order_status_success",
    "order_status_failed",
    "order_status_address_title",
    "order_status_desc",
    "order_datecreated",
    "order_code",
    "order_status",
  ]);

  const invoiceStatus = (
    status: number,
    deliveryStatus?: string
  ): {
    icon: React.ReactNode;
    title: string;
  } => {
    if (status === 15 && deliveryStatus) {
      return {
        icon: <PendingIcon className="w-6 h-6 min-w-[24px]" />,
        title: t("order_status_delivery"),
      };
    }
    switch (status) {
      case 15:
        return {
          icon: <PendingIcon className="w-6 h-6 min-w-[24px]" />,
          title: t("order_status_pending"),
        };
      case 19:
        return {
          icon: <SuccessIcon className="w-6 h-6 min-w-[24px]" />,
          title: t("order_status_success"),
        };
      case 21:
        return {
          icon: <CloseCircleFilledIcon className="min-w-[24px]" />,
          title: t("order_status_failed"),
        };
      default:
        return {
          icon: <></>,
          title: "",
        };
    }
  };

  return (
    <div className="text-sm leading-[1.6] text-gray-500 py-3 last:mb-[1px] rounded border border-gray-300">
      {/* Head */}
      <div
        className="flex items-center justify-between md:grid grid-cols-4 cursor-pointer px-6"
        onClick={onClick}
      >
        <div className="flex flex-col gap-1">
          <p className="font-bold min-h-[18px]">{t("order_datecreated")}</p>
          <p className="min-h-[18px]">
            <TextDateTime className="text-gray-500" ts={data.date_created} />
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <p className="font-bold min-h-[18px]">{t("order_code")}</p>
          <p className="min-h-[18px]">{data.code}</p>
        </div>
        <div className="flex flex-col gap-1">
          <p className="font-bold min-h-[18px]">{t("order_status")}</p>
          <p className="min-h-[18px]">
            {
              invoiceStatus(
                data.status,
                data.delivery_detail?.[data.delivery_detail.length - 1]?.action
              ).title
            }
          </p>
        </div>

        <div className="flex flex-col items-end justify-center">
          <span className="cursor-pointer">
            {open ? (
              <MinusIcon className="w-[24px] h-[24px] min-w-[24px] text-gray-500 hover:text-red-500 transition-all" />
            ) : (
              <PlusIcon className="w-[24px] h-[24px] min-w-[24px] text-gray-500 hover:text-red-500 transition-all" />
            )}
          </span>
        </div>
      </div>
      {/* body */}
      <div
        className={`transition-max-height linear duration-700 overflow-hidden ${
          open ? "max-h-[2000px]" : "max-h-0"
        }`}
      >
        {open && (
          <div className="border-t border-gray-300 mt-10">
            <div className="pt-4 pb-6 flex flex-col gap-2 items-center justify-center">
              {
                invoiceStatus(
                  data.status,
                  data.delivery_detail?.[data.delivery_detail.length - 1]
                    ?.action
                ).icon
              }
              <p className="text-base font-bold">
                {
                  invoiceStatus(
                    data.status,
                    data.delivery_detail?.[data.delivery_detail.length - 1]
                      ?.action
                  ).title
                }
              </p>
            </div>
          </div>
        )}
        {/* {open && <InvoicePublicDetails data={data} />} */}
      </div>
    </div>
  );
}
